import { useMemo } from "react";
import type { Identifier } from "ra-core";
import { useGetIdentity, useTranslate } from "ra-core";

import { useIsMobile } from "@/hooks/use-mobile";

import { MobileHomeHeader } from "../dashboard/MobileDashboard";
import {
  ListEmptyState,
  ListErrorState,
  ListRowsSkeleton,
} from "../design/ListStates";
import { MonthBar } from "../design/MonthBar";
import { ProgressBar } from "../design/ProgressBar";
import { MobileContent } from "../layout/MobileContent";
import { APP_LOCALE } from "../misc/locale";
import {
  selectDirectSalesInPeriod,
  sumDirectSalesAmountMxn,
} from "./directSales";
import { formatMxn, IncentiveBars } from "./IncentiveBars";
import {
  computeAdvisorGoalProgress,
  computeAdvisorProgramProgress,
} from "./incentiveProgress";
import type { LocalDate } from "./periods";
import { daysLeftInPeriod, getCurrentMonthPeriod } from "./periods";
import { useAdvisorProgress } from "./useAdvisorProgress";

/**
 * Almikia Epic E7 (TASK-092): the asesora's own "Mi avance" -- her month
 * against her own goal (design-system.md section 3: the month bar lives
 * only in the Dirección panel and here) plus one card per incentive program
 * she is eligible for (`IncentiveBars`). Reachable with one tap from Hoy
 * (`AdvisorToday.tsx`, TASK-089) in both shells, registered at its own
 * `/mi-avance` route in `CRM.tsx` so any role reaches her own advance the
 * same way she reaches her own Hoy. Always scoped to the signed-in user's
 * OWN records -- no ranking, no other advisor's data, no attendance/guardia/
 * meeting data anywhere (Epic E7 hard constraints).
 */

const MONTH_DATE_FORMAT = new Intl.DateTimeFormat(APP_LOCALE, {
  day: "numeric",
  month: "short",
});

function localDateToJsDate(date: LocalDate): Date {
  return new Date(date.year, date.month - 1, date.day);
}

export const MyProgressPage = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const { identity, isPending: isIdentityPending } = useGetIdentity();
  const salesId = identity?.id;
  const hasIdentity = Number.isInteger(salesId);
  const now = useMemo(() => new Date(), []);
  const period = useMemo(() => getCurrentMonthPeriod(now), [now]);
  const advisorIds = useMemo<Identifier[]>(
    () => (hasIdentity ? [salesId as Identifier] : []),
    [hasIdentity, salesId],
  );

  const { programs, goals, deals, tasks, isPending, error } =
    useAdvisorProgress(advisorIds, now);

  const monthSales = useMemo(
    () => selectDirectSalesInPeriod(deals, period),
    [deals, period],
  );
  const monthUnits = monthSales.length;
  const monthAmountMxn = sumDirectSalesAmountMxn(monthSales);

  const goalProgress = hasIdentity
    ? computeAdvisorGoalProgress(salesId as Identifier, deals, goals, period)
    : ({ status: "no_target" } as const);

  const programModels = hasIdentity
    ? computeAdvisorProgramProgress(programs, {
        advisorId: salesId as Identifier,
        advisorDeals: deals,
        advisorTasks: tasks,
        now,
      })
    : [];

  const hasUnitsGoal =
    goalProgress.status === "tracked" && goalProgress.targetUnits != null;
  const monthBarGoalPercent =
    goalProgress.status === "tracked" && goalProgress.targetUnits != null
      ? (goalProgress.actualUnits / goalProgress.targetUnits) * 100
      : 0;
  const daysLeft = daysLeftInPeriod(period, now);
  const monthStart = localDateToJsDate(period.start);
  const monthEnd = localDateToJsDate(period.end);

  const content = (
    <div className="flex flex-col gap-6 p-4">
      <h1 className="text-2xl font-bold">
        {translate("crm.my_progress.title")}
      </h1>

      <MonthBar
        currentDate={now}
        monthStart={monthStart}
        monthEnd={monthEnd}
        goalPercent={monthBarGoalPercent}
        showGoalMarker={hasUnitsGoal}
        startLabel={MONTH_DATE_FORMAT.format(monthStart)}
        endLabel={MONTH_DATE_FORMAT.format(monthEnd)}
        summaryLabel={translate("crm.my_progress.month_bar_summary", {
          smart_count: daysLeft,
        })}
      />

      {isIdentityPending || isPending ? (
        <ListRowsSkeleton rows={4} columns={2} />
      ) : error ? (
        <ListErrorState message={translate("crm.common.list_error")} />
      ) : (
        <>
          <section className="flex flex-col gap-3 rounded-2xl border bg-card p-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("crm.my_progress.goal_title")}
            </h2>
            {goalProgress.status === "no_target" ? (
              <>
                <p className="text-base font-semibold">
                  {translate("crm.my_progress.no_goal")}
                </p>
                <p className="text-base tabular-nums text-muted-foreground">
                  {translate("crm.my_progress.units_and_amount", {
                    units: monthUnits,
                    amount: formatMxn(monthAmountMxn),
                  })}
                </p>
              </>
            ) : (
              <>
                {goalProgress.targetUnits != null ? (
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between text-base tabular-nums">
                      <span>{translate("crm.my_progress.units_label")}</span>
                      <span>
                        {translate("crm.my_progress.units_progress", {
                          actual: goalProgress.actualUnits,
                          target: goalProgress.targetUnits,
                        })}
                      </span>
                    </div>
                    <ProgressBar
                      value={goalProgress.actualUnits}
                      max={Math.max(
                        goalProgress.targetUnits,
                        goalProgress.actualUnits,
                      )}
                      target={goalProgress.targetUnits}
                      label={translate("crm.my_progress.units_bar_label")}
                    />
                  </div>
                ) : null}
                {goalProgress.targetAmountMxn != null ? (
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between text-base tabular-nums">
                      <span>{translate("crm.my_progress.amount_label")}</span>
                      <span>
                        {translate("crm.my_progress.amount_progress", {
                          actual: formatMxn(goalProgress.actualAmountMxn),
                          target: formatMxn(goalProgress.targetAmountMxn),
                        })}
                      </span>
                    </div>
                    <ProgressBar
                      value={goalProgress.actualAmountMxn}
                      max={Math.max(
                        goalProgress.targetAmountMxn,
                        goalProgress.actualAmountMxn,
                      )}
                      target={goalProgress.targetAmountMxn}
                      label={translate("crm.my_progress.amount_bar_label")}
                    />
                  </div>
                ) : null}
              </>
            )}
          </section>

          {programModels.length === 0 ? (
            <ListEmptyState
              title={translate("crm.my_progress.empty_title")}
              description={translate("crm.my_progress.empty_description")}
            />
          ) : (
            <IncentiveBars models={programModels} />
          )}

          <p className="text-base text-muted-foreground">
            {translate("crm.my_progress.explanation")}
          </p>
        </>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <>
        <MobileHomeHeader />
        <MobileContent>{content}</MobileContent>
      </>
    );
  }

  return content;
};

MyProgressPage.path = "/mi-avance";
